import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { TitleService } from 'src/services/title.service';
import { AppNav } from 'src/routes/app-nav';
import { Confirmation, ConfirmationService, MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EngagementDTO } from 'src/interfaces/engagementDTO.interface';
import { EngagementService } from 'src/services/engagement.service';
import Helpers from 'src/shared/helpers';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiResponse } from '@/interfaces/apiresponse.interface';
import { BankAccountDTO } from '@/interfaces/bankAccountDTO.interface';
import { SubmitTransactionQueryReqDTO } from '@/interfaces/submitTransactionQueryReqDTO.interface';
import { TransactionQueryService } from '@/services/transaction-query.service';

@Component({
  selector: 'app-transaction-query-page',
  standalone: false,
  templateUrl: './transaction-query-page.component.html',
})
export class TransactionQueryPageComponent implements OnInit, OnDestroy {
  private destroyed = new Subject<any>();
  private helpers: Helpers;

  messageKey: string = 'global-error-slot';
  engagements: EngagementDTO[] = [];
  selectedEngagement!: EngagementDTO;
  vatNumbers: string[] = [];
  selectedVatNumber?: string;
  isValidVatNumber?: boolean;
  visibilityToggle: boolean = false;
  loading: boolean = false;
  submitEnabled: boolean = true;
  statusMonitorPageRoute: string =
    AppNav.StatusMonitor.route + '/' + AppNav.TransactionQueryStatus.route;

  bankAccounts: BankAccountDTO[] = [];
  selectedBankAccount?: BankAccountDTO;
  loadingBankAccounts: boolean = false;

  submitModel!: SubmitTransactionQueryReqDTO;

  datasetDateRange: string[] = [];
  powerBiDateRange: string[] = [];

  datePickerOptions: any[] = [];

  suggestedDateRange: string[] = [];
  suggestedDateRangeFor?: 'dataset' | 'powerbi';

  availableDateRangeText?: string;

  constructor(
    private titleService: TitleService,
    private engagementService: EngagementService,
    private confirmationService: ConfirmationService,
    private translocoService: TranslocoService,
    private messageService: MessageService,
    private transactionQueryService: TransactionQueryService,
  ) {
    this.helpers = Helpers.getInstance(this.translocoService);
  }

  ngOnInit(): void {
    this.titleService.setTitle(AppNav.TransactionQuery.title);
    this.resetSelectedEngagement();
    this.setNewModel();
    this.loadEngagements();
  }

  loadEngagements() {
    this.loading = true;
    this.engagementService.getAllDTO().subscribe({
      next: (engagements: EngagementDTO[]) => {
        this.engagements = engagements.map((x) => ({
          ...x,
          searchField: `${x.name} ${x.jobCode} ${x.vatNumbersWithDownloadType
            ?.map((r) => r.vatnumber)
            ?.join(' ')}`,
        }));
        this.loading = false;
      },
      error: (response: HttpErrorResponse) => {
        this.loading = false;
        console.error(response.error);
      },
    });
  }

  resetSelectedEngagement() {
    this.selectedEngagement = {
      name: undefined,
      jobCode: undefined,
      vatNumbersWithDownloadType: [],
      searchField: undefined,
    } as EngagementDTO;
  }

  setNewModel() {
    this.submitModel = {
      bankAccountId: undefined,
      engagementId: undefined,
      datasetRequested: false,
      datasetFromDate: undefined,
      datasetToDate: undefined,
      powerBiRequested: false,
      powerBiFromDate: undefined,
      powerBiToDate: undefined,
    };
    this.datasetDateRange = [];
    this.powerBiDateRange = [];
    this.bankAccounts = [];
    this.selectedBankAccount = undefined;
  }

  onEngagementNameListboxChange(event: any) {
    this.isValidVatNumber = undefined;
    this.selectedVatNumber = undefined;
    this.setNewModel();
    if (!event.value) {
      this.resetSelectedEngagement();
      return;
    }
    this.selectedEngagement = event.value;
    this.vatNumbers = this.selectedEngagement.vatNumbersWithDownloadType.map(
      (r) => r.vatnumber,
    );
  }

  onEngagementVatNumbersListboxChange(event: any) {
    this.messageService.clear();
    this.selectedBankAccount = undefined;
    this.bankAccounts = [];
    this.submitModel.datasetRequested = false;
    this.submitModel.powerBiRequested = false;
    this.datasetDateRange = [];
    this.powerBiDateRange = [];

    const vatNumber: string = event.value;
    this.isValidVatNumber = undefined;

    this.loadBankAccounts();
  }

  loadBankAccounts() {
    const technicalUserId =
      this.selectedEngagement.vatNumbersWithDownloadType.find(
        (r) => r.vatnumber === this.selectedVatNumber,
      )?.technicalUserId;

    if (!technicalUserId) return;

    this.loadingBankAccounts = true;
    this.transactionQueryService.getBankAccounts(technicalUserId).subscribe({
      next: (accounts: BankAccountDTO[]) => {
        this.bankAccounts = accounts;
        this.loadingBankAccounts = false;
        this.isValidVatNumber = true;
      },
      error: (response: HttpErrorResponse) => {
        this.loadingBankAccounts = false;
        this.isValidVatNumber = false;
        this.messageService.add({
          key: this.messageKey,
          severity: 'error',
          summary: this.translocoService.translate('error'),
          detail: this.translocoService.translate(
            'error_loading_bank_accounts',
          ),
          life: 30000,
        });
      },
    });
  }

  onBankAccountSelected(event: any) {
    this.selectedBankAccount = event.value;
  }

  onBankAccountCardSelected(account: BankAccountDTO) {
    this.selectedBankAccount = account;
    this.datePickerOptions = account.availableDates.map(x=> (    {
      start: new Date(x.availableFrom),
      end: new Date(x.availableTo)
    }));

    if (this.datePickerOptions) {
      const from = this.datePickerOptions[0].availableFrom;
      const to = this.datePickerOptions.reverse()[0].availableTo;
      const fmt = (d: Date) => d.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' });
      this.availableDateRangeText = `${fmt(from)} – ${fmt(to)}`;
    } else {
      this.availableDateRangeText = undefined;
    }
  }

  toggleDataset() {
    this.submitModel.datasetRequested = !this.submitModel.datasetRequested;
    if (
      this.submitModel.datasetRequested &&
      this.powerBiDateRange.length === 2 &&
      this.datasetDateRange.length === 0
    ) {
      this.suggestedDateRange = [...this.powerBiDateRange];
      this.suggestedDateRangeFor = 'dataset';
    } else {
      this.dismissSuggestedDateRange();
    }
  }

  showPowerBiUnavailableMessage() {
    const isHungarian = this.translocoService
      .getActiveLang()
      .startsWith('hu');

    this.messageService.add({
      key: this.messageKey,
      severity: 'warn',
      summary: isHungarian ? 'Power BI riport' : 'Power BI report',
      detail: isHungarian
        ? 'A Power BI riport jelenleg nem érhető el.'
        : 'The Power BI report is currently unavailable.',
      life: 5000,
    });
  }

  acceptSuggestedDateRange(target: 'dataset' | 'powerbi') {
    const values = [...this.suggestedDateRange];
    this.dismissSuggestedDateRange();
    setTimeout(() => {
      if (target === 'dataset') {
        this.datasetDateRange = values;
      } else {
        this.powerBiDateRange = values;
      }
    });
  }

  dismissSuggestedDateRange() {
    this.suggestedDateRange = [];
    this.suggestedDateRangeFor = undefined;
  }

  showSubmitConfirm() {
    if (!this.validateModel()) {
      return;
    }

    const confirmObj: Confirmation = {
      ...Helpers.getInstance(this.translocoService).getConfirmBase(
        'transactionQueryConfirm',
      ),
      header: this.translocoService.translate('confirm_transaction_query'),
      message: this.translocoService.translate(
        'confirm_transaction_query_message',
      ),
      acceptVisible: true,
      rejectVisible: true,
      closeOnEscape: true,
      accept: () => {
        this.submitQuery();
      },
    };

    this.confirmationService.confirm(confirmObj);
  }

  submitQuery() {
    this.messageService.clear();
    this.submitEnabled = false;

    this.submitModel.engagementId = this.selectedEngagement.id;
    this.submitModel.bankAccountId = this.selectedBankAccount?.bankAccountId;

    if (
      this.submitModel.datasetRequested &&
      this.datasetDateRange.length === 2
    ) {
      this.submitModel.datasetFromDate = this.toIsoDate(
        this.datasetDateRange[0],
      );
      this.submitModel.datasetToDate = this.toIsoDate(this.datasetDateRange[1]);
    }

    if (
      this.submitModel.powerBiRequested &&
      this.powerBiDateRange.length === 2
    ) {
      this.submitModel.powerBiFromDate = this.toIsoDate(
        this.powerBiDateRange[0],
      );
      this.submitModel.powerBiToDate = this.toIsoDate(this.powerBiDateRange[1]);
    }

    this.transactionQueryService.submitQuery(this.submitModel).subscribe({
      next: (response: ApiResponse) => {
        this.submitEnabled = true;
        this.setNewModel();
        this.messageService.add({
          key: this.messageKey,
          severity: 'success',
          summary: this.translocoService.translate('success'),
          detail: this.translocoService.translate('transaction_query_success'),
          life: 30000,
        });
      },
      error: (response: HttpErrorResponse) => {
        this.submitEnabled = true;
        this.messageService.add({
          key: this.messageKey,
          severity: 'error',
          summary: this.translocoService.translate('error'),
          detail: this.translocoService.translate('error_general'),
          life: 30000,
        });
      },
    });
  }

  reset() {
    this.isValidVatNumber = undefined;
    this.selectedVatNumber = undefined;
    this.resetSelectedEngagement();
    this.setNewModel();
    this.submitEnabled = true;

    this.visibilityToggle = true;
    setTimeout(() => {
      this.visibilityToggle = false;
    }, 10);
  }

  validateModel(): boolean {
    if (!this.selectedBankAccount) {
      this.messageService.add({
        key: this.messageKey,
        severity: 'error',
        summary: this.translocoService.translate('error'),
        detail: this.translocoService.translate(
          'error_no_bank_account_selected',
        ),
        life: 30000,
      });
      return false;
    }

    if (
      !this.submitModel.datasetRequested &&
      !this.submitModel.powerBiRequested
    ) {
      this.messageService.add({
        key: this.messageKey,
        severity: 'error',
        summary: this.translocoService.translate('error'),
        detail: this.translocoService.translate('error_nothing_selected'),
        life: 30000,
      });
      return false;
    }

    if (
      this.submitModel.datasetRequested &&
      (!this.datasetDateRange || this.datasetDateRange.length < 2)
    ) {
      this.messageService.add({
        key: this.messageKey,
        severity: 'error',
        summary: this.translocoService.translate('error'),
        detail: this.translocoService.translate('error_date_range'),
        life: 30000,
      });
      return false;
    }

    if (
      this.submitModel.powerBiRequested &&
      (!this.powerBiDateRange || this.powerBiDateRange.length < 2)
    ) {
      this.messageService.add({
        key: this.messageKey,
        severity: 'error',
        summary: this.translocoService.translate('error'),
        detail: this.translocoService.translate('error_bi_date_range'),
        life: 30000,
      });
      return false;
    }

    return true;
  }

  private toIsoDate(dateStr: string): string {
    const d = new Date(dateStr);
    const offset = d.getTimezoneOffset() * 60 * 1000;
    return new Date(d.getTime() - offset).toISOString();
  }

  ngOnDestroy(): void {
    this.destroyed.next(undefined);
    this.destroyed.complete();
  }
}
